import type { ColumnDef } from '@tanstack/react-table';
import type { BaseColumns, BaseColumn } from 'types/table';
import type { TableFilter } from 'features/misc/table';
import { generateColumns } from './generateColumns';
import { getComboBox } from 'utils/table';

import type {
  ActionsConfig,
  CustomColumnMeta,
  StandardColumnMeta,
  ExtendedColumnConfig,
  LinkColumnMeta,
  DrawerColumnMeta,
  EditableState,
  CustomColumn,
} from './columnModel';
import { editableRenderers, isSemanticBooleanType } from './columnConfig';
import type { RendererOverrides } from './columnConfig';
import { getLookupApi } from './columnTypes/registry';
import {
  createStandardActionsColumn,
  BULK_ACTIONS,
  getBulkActionText,
  getBulkActionIcon,
} from './actions';
import {
  createCustomColumn,
  createStandardColumnWithMeta,
  createLinkColumn,
  createDrawerColumn,
} from './columnFactories';
import { setCreateStandardActionsColumnFn } from './initDependencies';

// Re-export action utilities for backward compatibility
export { BULK_ACTIONS, getBulkActionText, getBulkActionIcon, createStandardActionsColumn };

// Re-export column factory functions for backward compatibility
export { createCustomColumn, createStandardColumnWithMeta, createLinkColumn, createDrawerColumn };

// Register createStandardActionsColumn with initDependencies for lazy initialization
setCreateStandardActionsColumnFn(createStandardActionsColumn);

type CreateColumnsOptions<T> = {
  cols: Record<string, BaseColumn | CustomColumn<T> | ExtendedColumnConfig<T>>;
  actions?: ActionsConfig<T>;
  columnOverrides?: Partial<Record<string, Partial<ColumnDef<T>>>>;
  dateFormatter?: (dateString: string | null) => string;
  editableState?: EditableState;
  setEditableState?: (state: EditableState) => void;
  rendererOverrides?: RendererOverrides;
};

/**
 * Creates table columns from a unified column configuration
 * Features only define column metadata - all rendering logic is handled here
 */
export const createColumns = <T extends Record<string, unknown>>(
  options: CreateColumnsOptions<T>
): ColumnDef<T>[] => {
  const { cols, actions, columnOverrides = {}, dateFormatter, rendererOverrides } = options;

  // Track column order and separate standard and custom columns
  const columnOrder: Array<{ key: string; isCustom: boolean }> = [];
  const standardCols: BaseColumns = {};
  const customColDefsMap = new Map<string, ColumnDef<T>>();

  Object.entries(cols).forEach(([key, col]) => {
    // Check for NEW unified pattern: editable: true/function + onUpdate + type has editable renderer
    // Supports both boolean (always editable) and function (conditionally editable per-row)
    const editableProp = 'editable' in col ? col.editable : undefined;
    // Check if the type has an editable renderer in the registry
    const hasEditableRenderer = col.type ? editableRenderers[col.type] != null : false;
    const isUnifiedEditable =
      'editable' in col &&
      (editableProp === true || typeof editableProp === 'function') &&
      'onUpdate' in col &&
      typeof col.onUpdate === 'function' &&
      hasEditableRenderer;

    // Check if it's a custom column (has customType) or uses unified editable pattern
    if ('customType' in col && col.customType) {
      const customCol = col as BaseColumn & CustomColumnMeta<T>;
      const columnDef = createCustomColumn<T>(key, customCol, rendererOverrides);
      customColDefsMap.set(key, columnDef);
      columnOrder.push({ key, isCustom: true });
    } else if (isUnifiedEditable) {
      // NEW: Unified editable pattern (type + editable: true + onUpdate)
      // renderColumnCell will detect this and use type-based renderer
      const customCol = col as unknown as BaseColumn & CustomColumnMeta<T>;
      const columnDef = createCustomColumn<T>(key, customCol, rendererOverrides);
      customColDefsMap.set(key, columnDef);
      columnOrder.push({ key, isCustom: true });
    } else if ('switchConfig' in col && col.switchConfig) {
      // Switch column with switchConfig - convert to switch format for StandardColumnMeta
      // NOTE: This must come BEFORE isSemanticBooleanType check because type: 'switch'
      // with switchConfig should use the MUI Switch component, not the semantic renderer
      const switchCol = col as BaseColumn & { switchConfig: StandardColumnMeta<T>['switch'] };
      const standardCol: BaseColumn & StandardColumnMeta<T> = {
        ...switchCol,
        switch: switchCol.switchConfig,
      };
      const columnDef = createStandardColumnWithMeta<T>(key, standardCol);
      customColDefsMap.set(key, columnDef);
      columnOrder.push({ key, isCustom: true });
    } else if (isSemanticBooleanType(col.type)) {
      // Auto-detect semantic boolean types (status, boolean) and treat as custom columns
      // This allows using type: 'status' without needing customType: 'status'
      const customCol = {
        ...col,
        customType: col.type, // Use type as customType for rendering
      } as BaseColumn & CustomColumnMeta<T>;
      const columnDef = createCustomColumn<T>(key, customCol, rendererOverrides);
      customColDefsMap.set(key, columnDef);
      columnOrder.push({ key, isCustom: true });
    } else if ('link' in col && col.link) {
      // Link column - renders as a navigation link
      const linkCol = col as BaseColumn & { link: LinkColumnMeta };
      const columnDef = createLinkColumn<T>(key, linkCol);
      customColDefsMap.set(key, columnDef);
      columnOrder.push({ key, isCustom: true });
    } else if ('drawer' in col && col.drawer) {
      // Drawer column - renders as a drawer trigger cell
      const drawerCol = col as BaseColumn & { drawer: DrawerColumnMeta<T> };
      const columnDef = createDrawerColumn<T>(key, drawerCol);
      customColDefsMap.set(key, columnDef);
      columnOrder.push({ key, isCustom: true });
    } else {
      // Standard column - check if it has metadata that needs special handling
      const standardCol = col as BaseColumn & StandardColumnMeta<T>;
      if (
        standardCol.switch ||
        standardCol.textWeight ||
        standardCol.minSize ||
        standardCol.size ||
        standardCol.primaryColumn
      ) {
        // Column has metadata that needs special rendering - create custom column def
        const columnDef = createStandardColumnWithMeta<T>(key, standardCol);
        customColDefsMap.set(key, columnDef);
        columnOrder.push({ key, isCustom: true });
      } else {
        // Pure standard column - add to standardCols
        standardCols[key] = col as BaseColumn;
        columnOrder.push({ key, isCustom: false });
      }
    }
  });

  // Generate standard columns using existing utility
  const generatedStandardCols = generateColumns<T>({
    cols: standardCols,
    customColumns: [],
    columnOverrides,
    dateFormatter,
  });

  // Create a map of generated standard columns by key for quick lookup
  const standardColsMap = new Map<string, ColumnDef<T>>();
  generatedStandardCols.forEach(col => {
    if (col.id) {
      standardColsMap.set(col.id, col);
    }
  });

  // Build final columns array in the original order
  const finalColumns: ColumnDef<T>[] = [];
  columnOrder.forEach(({ key, isCustom }) => {
    if (isCustom) {
      const customCol = customColDefsMap.get(key);
      if (customCol) {
        finalColumns.push(customCol);
      }
    } else {
      const standardCol = standardColsMap.get(key);
      if (standardCol) {
        finalColumns.push(standardCol);
      }
    }
  });

  // Add standard actions column at the end if handlers are provided
  if (actions && Object.keys(actions.handlers).length > 0) {
    finalColumns.push(createStandardActionsColumn<T>(actions));
  }

  return finalColumns;
};

// Filter types that are supported for filtering
const FILTERABLE_TYPES = [
  'text',
  'description',
  'dd',
  'tags-dd',
  'tags',
  'multiselect',
  'date',
  'dateRange',
  'number',
  'fraction',
  'status',
  'boolean',
  'switch',
] as const;

/**
 * Creates table filters from column configuration
 * Extracts columns with filterable types and converts them to TableFilter format
 * Accepts either a function that returns column config (like baseCols) or the config object directly
 *
 * Columns are included if:
 * - They have a valid filterable type (text, dd, date, etc.)
 * - They are NOT explicitly marked as filterable: false
 *
 * Note: customType is for rendering purposes only and does not affect filterability
 */
export const createFilters = <T, HandlerArg = unknown>(
  colsOrFn:
    | Record<
        string,
        BaseColumn | (BaseColumn & CustomColumnMeta<T>) | (BaseColumn & StandardColumnMeta<T>)
      >
    | ((
        handler: HandlerArg
      ) => Record<
        string,
        BaseColumn | (BaseColumn & CustomColumnMeta<T>) | (BaseColumn & StandardColumnMeta<T>)
      >)
): TableFilter[] => {
  // If it's a function, call it with a no-op handler (filters don't need handlers)
  const cols =
    typeof colsOrFn === 'function'
      ? colsOrFn((() => {
          // No-op handler for filters
        }) as HandlerArg)
      : colsOrFn;

  return Object.values(cols)
    .filter(c => {
      // Must have a type
      if (!('type' in c) || !c.type) return false;
      // Check if explicitly marked as not filterable
      if ('filterable' in c && c.filterable === false) return false;
      // Include if the type is a filterable type
      return (FILTERABLE_TYPES as readonly string[]).includes(c.type);
    })
    .map(c => {
      const col = c as BaseColumn;
      // Use filterType if specified, otherwise use type
      const effectiveType = col.filterType || col.type;
      if (
        effectiveType === 'dd' ||
        effectiveType === 'tags-dd' ||
        effectiveType === 'multiselect'
      ) {
        // Resolve API: explicit col.api > lookup registry
        const resolvedApi = col.api || getLookupApi(col.id);
        return {
          ...col,
          type: effectiveType, // Override type for filter
          component: getComboBox(col.id, col.label, resolvedApi, col.options),
          ...(resolvedApi ? { api: resolvedApi } : {}),
          options: col.options,
        };
      }
      // For non-dropdown types, still apply filterType override if specified
      return {
        ...col,
        type: effectiveType, // Override type for filter (e.g., date -> dateRange)
      };
    }) as TableFilter[];
};
